import api from "./Api"

// ── Module ID Constants ──
export const MODULE = {
  DASHBOARD: 1,
  COMPANY: 2, PLANTS: 3, CHILLING_CENTERS: 4, ROUTES: 5,
  COLLECTION_CENTERS: 6, BULK_VENDORS: 7, PRODUCERS: 8,
  MATERIALS: 9, STAFFS: 10, TRANSPORTERS: 11, VEHICLES: 12,
  RATE_CHARTS: 13, INCENTIVE_CHARTS: 14, CC_INCENTIVES: 15,
  VLCC_INCENTIVES: 16, VLCC_PURCHASES: 17, CC_PURCHASES: 18,
  CC_SPOILED: 19, SESSION_STATUS: 20,
  ROUTE_BILLS: 21, TRANSPORT_BILLS: 22, BONUS_BILLS: 23,
  VENDOR_LOANS: 24, VENDOR_CASH_ADV: 25, VENDOR_MATERIAL_ADV: 26,
  PRODUCER_LOANS: 27, PRODUCER_CASH_ADV: 28, PRODUCER_MATERIAL_ADV: 29,
  VENDOR_RECOVERIES: 30, PRODUCER_RECOVERIES: 31,
  MAP_VIEW: 32, BILL_CUSTOMIZATION: 33, ROLES_USERS: 34,
  CONFIGURATIONS: 35, REPORTS: 36, GRAPH_REPORTS: 37,
  PAYMENTS: 38, SMS_SETTINGS: 39, VEHICLE_CASH_ADV: 40,
  CENTER_MILK_SALES: 41, MILK_TRANSFERS: 42,
}

// ── Action bit positions ──
const ACTION_INDEX = { view: 0, add: 1, edit: 2, delete: 3, export: 4, approve: 5, statusupdate: 6 }

// ── Auth user from localStorage ──
let user = null
try { user = JSON.parse(localStorage.getItem("authUser")) } catch (e) { /* ignore */ }
export const AuthUser = user || false
export const UserAccess = user ? user.accessLevel || 0 : false

// ── Permission cache ──
let cachedPermissions = null
let cachedRoleSettings = null
let permissionPromise = null

/**
 * Load role data from API and parse role_settings JSON bitmask.
 * Uses localStorage cache for instant load — refreshes from API in background.
 * Returns { roleData, roleSettings } where roleSettings is { moduleId: "1110100", ... }
 */
export const loadPermissions = async () => {
  if (cachedPermissions && cachedRoleSettings) {
    return { roleData: cachedPermissions, roleSettings: cachedRoleSettings }
  }

  // ── Try localStorage cache first (instant, no API wait) ──
  try {
    const cached = localStorage.getItem("dairy_permissions_cache")
    if (cached) {
      const parsed = JSON.parse(cached)
      if (parsed && parsed.roleData && parsed.roleSettings) {
        cachedPermissions = parsed.roleData
        cachedRoleSettings = parsed.roleSettings
        console.log("%c⚡ Permissions loaded from cache (instant)", "color: #22c55e; font-weight: bold")

        // Refresh from API in background (non-blocking)
        setTimeout(() => refreshPermissionsFromAPI(), 100)
        return { roleData: cachedPermissions, roleSettings: cachedRoleSettings }
      }
    }
  } catch (e) { /* cache miss, proceed to API */ }

  // ── No cache — must fetch from API (blocking) ──
  // Prevent duplicate concurrent requests
  if (permissionPromise) return permissionPromise
  permissionPromise = fetchAndCachePermissions()
  const result = await permissionPromise
  permissionPromise = null
  return result
}

/**
 * Fetch permissions from API and save to localStorage cache.
 */
const fetchAndCachePermissions = async () => {
  const info = JSON.parse(localStorage.getItem("authUser"))
  if (!info || !info.role) return { roleData: null, roleSettings: {} }

  try {
    const resp = await api.get("role/" + info.role)
    if (resp.data && resp.data.data && resp.data.data.length > 0) {
      const roleData = resp.data.data[0]
      cachedPermissions = roleData

      // Parse role_settings JSON string into object
      let settings = {}
      try {
        const raw = roleData.role_settings
        if (typeof raw === "string" && raw.length > 0) {
          settings = JSON.parse(raw)
        } else if (typeof raw === "object" && raw !== null) {
          settings = raw
        }
      } catch (e) { /* invalid JSON, use empty */ }
      cachedRoleSettings = settings

      // Save to localStorage for instant load next time
      try {
        localStorage.setItem("dairy_permissions_cache", JSON.stringify({
          roleData, roleSettings: settings, cachedAt: Date.now()
        }))
      } catch (e) { /* storage full, ignore */ }

      return { roleData, roleSettings: settings }
    }
  } catch (e) {
    console.error("Failed to load permissions:", e)
  }
  return { roleData: null, roleSettings: {} }
}

/**
 * Background refresh — updates cache without blocking UI.
 */
const refreshPermissionsFromAPI = async () => {
  try {
    const result = await fetchAndCachePermissions()
    if (result.roleData) {
      console.log("%c🔄 Permissions refreshed from API (background)", "color: #6366f1")
    }
  } catch (e) { /* non-blocking, ignore */ }
}

/**
 * Check if current user has a specific permission.
 * @param {number} moduleId - Module ID from MODULE constants
 * @param {string} action - Action name: 'view', 'add', 'edit', 'delete', 'export', 'approve', 'statusupdate'
 * @returns {boolean}
 */
export const hasPermission = (moduleId, action = "view") => {
  // If no permissions loaded yet, allow (will be checked after load)
  if (!cachedPermissions) return true

  // Admin (access_level 7 or "Admin") always has full access
  const level = cachedPermissions.access_level
  if (level === 7 || level === "7" || level === "Admin") return true

  // Check bitmask
  const bits = cachedRoleSettings?.[String(moduleId)]
  if (!bits) return false

  const idx = ACTION_INDEX[action]
  if (idx === undefined) return false
  return bits[idx] === "1"
}

// ── Convenience helpers ──
export const canView = (moduleId) => hasPermission(moduleId, "view")
export const canAdd = (moduleId) => hasPermission(moduleId, "add")
export const canEdit = (moduleId) => hasPermission(moduleId, "edit")
export const canDelete = (moduleId) => hasPermission(moduleId, "delete")
export const canExport = (moduleId) => hasPermission(moduleId, "export")
export const canApprove = (moduleId) => hasPermission(moduleId, "approve")
export const canStatusUpdate = (moduleId) => hasPermission(moduleId, "statusupdate")

/**
 * Legacy compatibility: getInfo() — returns role data array
 */
export const getInfo = async () => {
  const { roleData } = await loadPermissions()
  return roleData ? [roleData] : []
}

/**
 * Legacy compatibility: getPermissions() — returns single role object
 */
export const getPermissions = async () => {
  const { roleData } = await loadPermissions()
  return roleData || null
}

/**
 * Clear permission cache (call on logout or role change).
 */
export const clearPermissionCache = () => {
  cachedPermissions = null
  cachedRoleSettings = null
  permissionPromise = null
  try { localStorage.removeItem("dairy_permissions_cache") } catch (e) { /* ignore */ }
}
